/**
 * Cryptographically-strong randomness.
 *
 * `Math.random()` is not seeded from a CSPRNG, so its output is predictable
 * from enough samples (CWE-338). Nothing in the app uses it — not even for an
 * id whose only job is to be unique, because "this one is harmless" is exactly
 * how a weak value ends up load-bearing later. `crypto.getRandomValues` is
 * available in every browser this app supports and in the Node runtime Next
 * renders with, so there is no cost to defaulting to it.
 */

/** Uniform float in `[0, 1)` — the drop-in replacement for `Math.random()`. */
export function randomFraction(): number {
  const [value] = crypto.getRandomValues(new Uint32Array(1));
  return value / 2 ** 32;
}

/** Lowercase hex token of `bytes * 2` characters. */
export function randomToken(bytes = 8): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(bytes)), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');
}

/**
 * Opaque unique id. Prefers `crypto.randomUUID`, which needs a secure context;
 * the token fallback covers a plain-http origin and is drawn from the same
 * CSPRNG, so neither branch is a weak PRNG.
 */
export function randomId(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : randomToken(16);
}
